/**
 * 「已经发出验证码、等着填」这个状态要活过一次页面重载。
 *
 * ★ 为什么必须落盘：用户得切到邮箱 app 去抄那 6 位数字，而微信 WKWebView 在后台被
 *   回收重载是常态（见 docs/DEVICES.md）。回来时码还在有效期内，界面却回到了第一屏 ——
 *   他唯一的出路是重新发一封，而重发会把收件箱里那枚**还有效**的码顶掉。
 *
 * ★ 只存「发给了哪个邮箱、什么时候过期、什么时候能重发」。
 *   验证码和密码一个字都不存 —— 它们是能换到账号的东西，落盘等于把风险从
 *   「服务器被拖库」扩大到「这台手机被人拿去看一眼」。
 */
const KEY = 'poker.pendingBind.v1'

export interface PendingBind {
  /** 绑给哪个账号。与当前账号对不上就整条作废——换过身份之后那枚码按账号定位，对不上 */
  readonly accountId: string
  readonly email: string
  readonly expiresAt: number
  readonly resendAt: number
}

interface StoreLike {
  getItem(k: string): string | null
  setItem(k: string, v: string): void
  removeItem(k: string): void
}

/** 无痕模式、关掉站点数据、缩略图截取……读写都可能直接抛，一律吞掉 */
function safeStore(): StoreLike | null {
  try {
    const s = globalThis.localStorage as StoreLike | undefined
    return s ?? null
  } catch {
    return null
  }
}

export function savePendingBind(v: PendingBind): void {
  const s = safeStore()
  if (s === null) return
  try {
    s.setItem(KEY, JSON.stringify(v))
  } catch {
    /* 存不下就算了：丢的只是便利，不是正确性 */
  }
}

export function clearPendingBind(): void {
  const s = safeStore()
  if (s === null) return
  try {
    s.removeItem(KEY)
  } catch {
    /* 同上 */
  }
}

/**
 * 读回来。任何一条对不上就当没有：
 * 账号变了、已经过期、格式不认识 —— 都不如干干净净地从第一屏开始。
 */
export function loadPendingBind(accountId: string, now: number): PendingBind | null {
  const s = safeStore()
  if (s === null) return null
  let raw: string | null = null
  try {
    raw = s.getItem(KEY)
  } catch {
    return null
  }
  if (raw === null || raw === '') return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    clearPendingBind()
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) {
    clearPendingBind()
    return null
  }
  const v = parsed as Partial<PendingBind>
  if (
    typeof v.accountId !== 'string' ||
    typeof v.email !== 'string' ||
    typeof v.expiresAt !== 'number' ||
    typeof v.resendAt !== 'number' ||
    v.email === ''
  ) {
    clearPendingBind()
    return null
  }
  // ★ 身份换过人（邮箱登录、兑换邮件链接）之后，那枚码是按**旧账号**签的，对不上
  if (v.accountId !== accountId || v.expiresAt <= now) {
    clearPendingBind()
    return null
  }
  return { accountId: v.accountId, email: v.email, expiresAt: v.expiresAt, resendAt: v.resendAt }
}
